import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { api, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PROPERTY_STATUS, CLIENT_STATUS, canCreate, isManager, numOrNull } from "../lib/re";
import { Badge, Spinner, Tabs, Btn, Timeline } from "../components/re/ui";
import { GeneralTab, LocationTab, FeaturesTab } from "../components/re/PropertyForm";
import { Gallery } from "../components/re/Gallery";
import { PropertyDocs } from "../components/re/PropertyDocs";
import { OwnerForm } from "../components/re/OwnerForm";
import { AiTextPanel } from "../components/re/AiText";
import { toast } from "sonner";
import { Save, Copy, Archive, ArchiveRestore, Trash2, ArrowLeft, UserPlus, X, Search } from "lucide-react";

const FIELDS = ["title", "property_type", "purpose", "status", "acquisition_date", "consultant_id", "acquisition_source",
  "description", "title_en", "description_en", "internal_notes", "country", "district", "municipality", "parish", "address", "postal_code", "latitude",
  "longitude", "show_exact_address", "sale_price", "rent_price", "area_private", "area_construction", "area_useful",
  "area_land", "bedrooms", "bathrooms", "floors", "year_built", "conservation", "energy_class", "energy_cert_expiry",
  "parking_spaces", "garage", "elevator", "balcony", "terrace", "garden", "pool", "storage_room", "furnished", "equipment",
  "sun_exposure", "accessibility", "custom_features", "video_url", "virtual_tour_url", "published"];
const NUMERIC = ["latitude", "longitude", "sale_price", "rent_price", "area_private", "area_construction", "area_useful",
  "area_land", "bedrooms", "bathrooms", "floors", "year_built", "parking_spaces"];

function toPayload(p, canPrivate) {
  const out = {};
  FIELDS.forEach((k) => {
    if (!(k in p)) return;
    if (k === "internal_notes" && !canPrivate) return;
    let v = p[k];
    if (NUMERIC.includes(k)) v = numOrNull(v);
    else if (k === "custom_features") v = (v || []).map((s) => s.trim()).filter(Boolean);
    else if (typeof v === "string" && v === "" && !["title", "property_type", "purpose", "status"].includes(k)) v = null;
    out[k] = v;
  });
  return out;
}

function OwnersTab({ property, editable, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [modal, setModal] = useState(false);
  const ids = (property.owners || []).map((o) => o.id);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => api.get("/re/owners", { params: { q } }).then((r) => setResults(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const setOwners = async (owner_ids) => {
    try { await api.patch(`/re/properties/${property.id}`, { owner_ids }); toast.success("Proprietários atualizados"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao atualizar"); }
  };

  return (
    <div className="space-y-4">
      {(property.owners || []).length === 0 && <p className="text-sm text-slate-500">Nenhum proprietário associado.</p>}
      {(property.owners || []).map((o) => (
        <div key={o.id} className="card-almar p-4 flex items-center justify-between" data-testid={`linked-owner-${o.id}`}>
          <div><Link to={`/proprietarios?id=${o.id}`} className="text-slate-100 font-medium hover:text-[#E0C574]">{o.name}</Link>
            <p className="text-xs text-slate-500">{[o.phone, o.email].filter(Boolean).join(" · ")}</p></div>
          {editable && <button onClick={() => setOwners(ids.filter((i) => i !== o.id))} data-testid={`unlink-owner-${o.id}`} className="text-slate-400 hover:text-red-400"><X size={16} /></button>}
        </div>
      ))}
      {editable && (
        <div className="card-almar p-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input className="input-almar pl-9" placeholder="Pesquisar proprietário por nome, NIF ou contacto" value={q} onChange={(e) => setQ(e.target.value)} data-testid="owner-link-search" />
            </div>
            <Btn variant="ghost" onClick={() => setModal(true)} data-testid="owner-quick-create-btn"><UserPlus size={15} /> Novo proprietário</Btn>
          </div>
          {results.filter((r) => !ids.includes(r.id)).map((r) => (
            <button key={r.id} onClick={() => { setOwners([...ids, r.id]); setQ(""); }} data-testid={`owner-link-option-${r.id}`}
              className="w-full text-left mt-2 px-3 py-2 rounded-lg hover:bg-[#0D1B2A] text-sm text-slate-200">{r.name} <span className="text-xs text-slate-500">{r.email || r.phone}</span></button>
          ))}
        </div>
      )}
      {modal && <OwnerForm onClose={() => setModal(false)} onSaved={(o) => { setModal(false); setOwners([...ids, o.id]); }} />}
    </div>
  );
}

const NEW_DOC_CATS = [
  ["Caderneta predial urbana", false], ["Certidão predial", false], ["Certificado energético", false],
  ["Documento de identificação", true], ["Licença de habitação", false], ["Plantas", true], ["Outros", true],
];

function PendingDocs({ docs, setDocs }) {
  const add = (category, list) => setDocs([...docs, ...Array.from(list || []).map((file) => ({ category, file }))]);
  return (
    <div className="space-y-3" data-testid="pending-docs">
      <p className="text-xs text-slate-500">Os documentos são carregados ao criar o imóvel e ficam "Por verificar" até serem validados por um utilizador autorizado.</p>
      {NEW_DOC_CATS.map(([cat, multi], ci) => {
        const mine = docs.map((d, i) => ({ ...d, i })).filter((d) => d.category === cat);
        return (
          <div key={cat} className="card-almar p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`pending-doc-cat-${ci}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-100">{cat}{cat.startsWith("Documento de identificação") ? " do(s) proprietário(s) — 1 ou mais" : ""}</p>
              {mine.map((d) => (
                <p key={d.i} className="text-xs text-slate-400 flex items-center gap-2 mt-1">{d.file.name}
                  <button onClick={() => setDocs(docs.filter((_, j) => j !== d.i))} className="text-slate-500 hover:text-red-400" data-testid={`pending-doc-remove-${d.i}`}><X size={12} /></button></p>
              ))}
            </div>
            {(multi || mine.length === 0) && (
              <label className="text-xs border border-[#1E3A56] hover:border-[#C9A84C]/60 text-slate-300 rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap">
                + Adicionar ficheiro
                <input type="file" hidden multiple={multi} accept="application/pdf,image/*,.doc,.docx" data-testid={`pending-doc-input-${ci}`} onChange={(e) => { add(cat, e.target.files); e.target.value = ""; }} />
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PendingPhotos({ files, setFiles }) {
  const add = (list) => setFiles([...files, ...Array.from(list || []).filter((f) => f.type.startsWith("image/")).map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  return (
    <div data-testid="pending-photos">
      <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="block border-2 border-dashed border-[#1E3A56] hover:border-[#C9A84C]/60 rounded-lg py-6 text-center text-sm text-slate-300 cursor-pointer mb-4">
        Selecionar ou arrastar fotografias — serão carregadas ao criar o imóvel
        <input type="file" multiple accept="image/*" hidden data-testid="pending-photos-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </label>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {files.map((x, i) => (
          <div key={x.url} className="relative aspect-[4/3] rounded-lg overflow-hidden" data-testid={`pending-photo-${i}`}>
            <img src={x.url} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute top-2 left-2 text-[10px] bg-[#C9A84C] text-[#0D1B2A] font-bold px-2 py-0.5 rounded">PRINCIPAL</span>}
            <button onClick={() => { URL.revokeObjectURL(x.url); setFiles(files.filter((_, j) => j !== i)); }} className="absolute top-2 right-2 bg-black/60 rounded-full p-1 text-white" data-testid={`pending-photo-remove-${i}`}><X size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchingClients({ pid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/re/properties/${pid}/matching-clients`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [pid]);
  if (!rows) return <Spinner />;
  if (!rows.length) return <p className="text-sm text-slate-500">Nenhum cliente compatível na sua carteira.</p>;
  return (
    <div className="space-y-2" data-testid="matching-clients">
      {rows.map((c) => (
        <Link key={c.id} to={`/clientes?id=${c.id}`} className="card-almar p-4 flex items-center justify-between gap-3">
          <div className="min-w-0"><p className="text-slate-100 font-medium truncate">{c.name}</p><p className="text-xs text-slate-500 truncate">{c.reasons.join(" · ")}</p></div>
          <div className="flex items-center gap-3"><Badge value={c.status} label={CLIENT_STATUS[c.status]} /><span className="text-[#E0C574] font-semibold text-sm">{c.score}%</span></div>
        </Link>
      ))}
    </div>
  );
}

export default function ImovelFicha() {
  const { id } = useParams();
  const isNew = id === "novo";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(isNew ? { status: "em_preparacao", country: "Portugal", custom_features: [] } : null);
  const [tab, setTab] = useState("geral");
  const [pending, setPending] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (isNew) return;
    api.get(`/re/properties/${id}`).then((r) => setP(r.data)).catch(() => { toast.error("Imóvel não encontrado"); navigate("/imoveis"); });
  }, [id, isNew, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active))).catch(() => {}); }, []);

  if (!p) return <Layout title="Imóvel"><Spinner /></Layout>;
  if (isNew && !canCreate(user)) return <Layout title="Imóvel"><p className="text-slate-400">Sem permissão para criar imóveis.</p></Layout>;

  const editable = isNew || p.can_edit;
  const canPrivate = isNew || p.can_private;

  const save = async () => {
    if (!p.title?.trim() || !p.property_type || !p.purpose) { toast.error("Preencha o título, o tipo e a finalidade"); setTab("geral"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post("/re/properties", toPayload(p, true));
        toast.success(`Imóvel ${r.data.reference} criado`);
        if (pending.length) {
          const fd = new FormData();
          pending.forEach((x) => fd.append("files", x.file));
          fd.append("kind", "foto"); fd.append("category", "Geral");
          try { await api.post(`/re/properties/${r.data.id}/media`, fd); toast.success(`${pending.length} fotografia(s) carregada(s)`); }
          catch (e) { toast.error(e?.response?.data?.detail || "Imóvel criado, mas houve erro ao carregar as fotografias"); }
          pending.forEach((x) => URL.revokeObjectURL(x.url));
          setPending([]);
        }
        if (pendingDocs.length) {
          let ok = 0;
          for (const d of pendingDocs) {
            const fd = new FormData();
            fd.append("category", d.category); fd.append("file", d.file); fd.append("name", d.file.name);
            try { await api.post(`/re/properties/${r.data.id}/documents`, fd); ok += 1; }
            catch (e) { toast.error(`${d.file.name}: ${e?.response?.data?.detail || "erro ao carregar"}`); }
          }
          if (ok) toast.success(`${ok} documento(s) carregado(s)`);
          setPendingDocs([]);
        }
        setTab("galeria");
        navigate(`/imoveis/${r.data.id}`, { replace: true });
      } else {
        const r = await api.patch(`/re/properties/${id}`, toPayload(p, canPrivate));
        setP(r.data); toast.success("Alterações guardadas");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao guardar"); }
    setSaving(false);
  };
  const action = async (fn, msg, after) => { try { const r = await fn(); toast.success(msg); after ? after(r) : load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); } };

  const tabs = [
    { id: "geral", label: "Identificação" }, { id: "localizacao", label: "Localização" }, { id: "caracteristicas", label: "Características" },
    { id: "galeria", label: "Galeria", count: isNew ? pending.length : p.media?.length || 0 },
    ...(isNew ? [{ id: "documentacao", label: "Documentação", count: pendingDocs.length }] : []),
    ...(!isNew && canPrivate ? [{ id: "documentacao", label: "Documentação", count: p.docs?.missing?.length ? `${p.docs.missing.length} em falta` : null },
      { id: "proprietarios", label: "Proprietários", count: p.owners?.length || 0 }] : []),
    ...(!isNew ? [{ id: "clientes", label: "Clientes compatíveis" }] : []),
    ...(!isNew && canPrivate ? [{ id: "historico", label: "Histórico" }] : []),
  ];

  return (
    <Layout title={isNew ? "Novo Imóvel" : "Ficha do Imóvel"}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
        <div className="min-w-0">
          <button onClick={() => navigate("/imoveis")} className="text-xs text-slate-400 hover:text-[#E0C574] flex items-center gap-1 mb-1" data-testid="back-to-properties"><ArrowLeft size={13} /> Imóveis</button>
          <div className="flex items-center gap-3 flex-wrap">
            {p.reference && <span className="font-mono text-xs text-slate-400" data-testid="property-reference">{p.reference}</span>}
            {p.status && <Badge value={p.status} label={PROPERTY_STATUS[p.status]} testId="property-status-badge" />}
            {p.archived && <span className="text-xs text-red-300">Arquivado</span>}
          </div>
          <h2 className="text-xl sm:text-2xl text-slate-100 font-semibold truncate mt-1" data-testid="property-title">{p.title || "Sem título"}</h2>
          {!isNew && <p className="text-sm text-[#E0C574]">{p.sale_price ? eur(p.sale_price) : ""}{p.sale_price && p.rent_price ? " · " : ""}{p.rent_price ? `${eur(p.rent_price)}/mês` : ""}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew && canCreate(user) && canPrivate && <Btn variant="ghost" onClick={() => action(() => api.post(`/re/properties/${id}/duplicate`), "Imóvel duplicado", (r) => navigate(`/imoveis/${r.data.id}`))} data-testid="duplicate-property-btn"><Copy size={15} /> Duplicar</Btn>}
          {!isNew && editable && <Btn variant="ghost" onClick={() => action(() => api.post(`/re/properties/${id}/archive`, { archived: !p.archived }), p.archived ? "Imóvel reativado" : "Imóvel arquivado")} data-testid="archive-property-btn">{p.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />} {p.archived ? "Reativar" : "Arquivar"}</Btn>}
          {!isNew && user.role === "admin" && <Btn variant="danger" onClick={() => window.confirm("Eliminar definitivamente este imóvel?") && action(() => api.delete(`/re/properties/${id}`), "Imóvel eliminado", () => navigate("/imoveis"))} data-testid="delete-property-btn"><Trash2 size={15} /></Btn>}
          {editable && (["geral", "localizacao", "caracteristicas"].includes(tab) || (isNew && ["galeria", "documentacao"].includes(tab))) && <Btn onClick={save} disabled={saving} data-testid="save-property-btn"><Save size={15} /> {saving ? "A guardar..." : isNew ? "Criar imóvel" : "Guardar"}</Btn>}
        </div>
      </div>
      {!isNew && editable && <AiTextPanel property={p} setP={setP} />}
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      <div className="card-almar p-5">
        {tab === "geral" && <GeneralTab p={p} set={setP} ro={!editable} users={users} isManager={isManager(user)} canPrivate={canPrivate} />}
        {tab === "localizacao" && <LocationTab p={p} set={setP} ro={!editable} />}
        {tab === "caracteristicas" && <FeaturesTab p={p} set={setP} ro={!editable} />}
        {tab === "galeria" && (isNew ? <PendingPhotos files={pending} setFiles={setPending} /> : <Gallery property={p} editable={editable} reload={load} />)}
        {tab === "documentacao" && (isNew ? <PendingDocs docs={pendingDocs} setDocs={setPendingDocs} /> : <PropertyDocs property={p} onChange={load} />)}
        {tab === "proprietarios" && <OwnersTab property={p} editable={editable} onChange={load} />}
        {tab === "clientes" && <MatchingClients pid={id} />}
        {tab === "historico" && <Timeline items={p.history} testId="property-history" />}
      </div>
    </Layout>
  );
}
